import React, { useState, useEffect } from 'react';
import { StaticIP, staticIpApi, IPRange } from '../services/api';
import StaticIPForm from './StaticIPForm';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './StaticIPList.css';

interface StaticIPListProps {
  ipRange: IPRange;
  onUpdate?: () => void;
  searchQuery?: string;
}

const StaticIPList: React.FC<StaticIPListProps> = ({ ipRange, onUpdate, searchQuery = '' }) => {
  const { canEdit } = useAuth();
  const [staticIPs, setStaticIPs] = useState<StaticIP[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIP, setEditingIP] = useState<StaticIP | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; ip: StaticIP | null }>({
    isOpen: false,
    ip: null
  });

  useEffect(() => {
    loadStaticIPs();
  }, [ipRange]);

  const loadStaticIPs = async () => {
    try {
      setLoading(true);
      const isActive = filter === 'all' ? undefined : filter === 'active';
      const response = await staticIpApi.getAll(ipRange.id, isActive);
      setStaticIPs(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load static IPs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaticIPs();
  }, [filter]);

  const handleCreateIP = () => {
    setEditingIP(null);
    setShowForm(true);
  };

  const handleEditIP = (ip: StaticIP) => {
    setEditingIP(ip);
    setShowForm(true);
  };

  const handleDeleteIP = (ip: StaticIP) => {
    setDeleteConfirm({ isOpen: true, ip });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.ip) return;
    
    try {
      await staticIpApi.delete(deleteConfirm.ip.id);
      await loadStaticIPs();
      if (onUpdate) onUpdate();
      setDeleteConfirm({ isOpen: false, ip: null });
    } catch (err) {
      setError('Failed to delete static IP');
      console.error(err);
      setDeleteConfirm({ isOpen: false, ip: null });
    }
  };

  const handleToggleActive = async (ip: StaticIP) => {
    try {
      await staticIpApi.update(ip.id, { is_active: !ip.is_active });
      await loadStaticIPs();
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Failed to update static IP');
      console.error(err);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingIP(null);
  };

  const handleFormSubmit = async () => {
    await loadStaticIPs();
    if (onUpdate) onUpdate();
    handleFormClose();
  };

  const getDeviceTypeIcon = (type?: string): string => {
    switch (type?.toLowerCase()) {
      case 'server': return '🖥️';
      case 'printer': return '🖨️';
      case 'network device': return '🔌';
      case 'workstation': return '💻';
      default: return '📟';
    }
  };

  return (
    <div className="static-ip-list">
      <div className="static-ip-header">
        <h3>Static IP Assignments</h3>
        <div className="header-controls">
          <div className="filter-buttons">
            <button
              className={`btn btn-sm ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`btn btn-sm ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              Active
            </button>
            <button
              className={`btn btn-sm ${filter === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilter('inactive')}
            >
              Inactive
            </button>
          </div>
          {canEdit() && (
            <button className="btn btn-primary" onClick={handleCreateIP}>
              Assign IP
            </button>
          )}
        </div>
      </div>

      <div className="ip-range-info">
        <span>Range: {ipRange.network}/{ipRange.subnet_mask}</span>
        {ipRange.static_start && ipRange.static_end && (
          <span>Static Pool: {ipRange.static_start} - {ipRange.static_end}</span>
        )}
      </div>

      {loading && <div className="loading">Loading static IPs...</div>}
      {error && <div className="error">{error}</div>}

      <div className="static-ip-table">
        <table>
          <thead>
            <tr>
              <th>IP Address</th>
              <th>Device Name</th>
              <th>Type</th>
              <th>MAC Address</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staticIPs
              .filter((ip) => {
                if (!searchQuery) return true;
                const query = searchQuery.toLowerCase();
                
                // Check regular fields
                if (ip.ip_address.includes(query) ||
                    ip.device_name?.toLowerCase().includes(query) ||
                    ip.device_type?.toLowerCase().includes(query) ||
                    ip.description?.toLowerCase().includes(query)) {
                  return true;
                }
                
                // Special handling for MAC address - normalize both query and stored MAC
                if (ip.mac_address) {
                  const normalizedQuery = query.toUpperCase().replace(/[^0-9A-F]/g, '');
                  const normalizedMac = ip.mac_address.toUpperCase().replace(/[^0-9A-F]/g, '');
                  return normalizedMac.includes(normalizedQuery);
                }
                
                return false;
              })
              .map((ip) => (
              <tr key={ip.id} className={!ip.is_active ? 'inactive' : ''}>
                <td className="ip-address">{ip.ip_address}</td>
                <td>
                  {ip.device_name && (
                    <>
                      <span className="device-icon">{getDeviceTypeIcon(ip.device_type)}</span>
                      {ip.device_name}
                    </>
                  )}
                </td>
                <td>{ip.device_type}</td>
                <td className="mac-address">{ip.mac_address}</td>
                <td>{ip.description}</td>
                <td>
                  <span className={`status ${ip.is_active ? 'active' : 'inactive'}`}>
                    {ip.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {canEdit() ? (
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm"
                        onClick={() => handleToggleActive(ip)}
                        title={ip.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {ip.is_active ? '⏸️' : '▶️'}
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleEditIP(ip)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteIP(ip)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </td>
              </tr>
            ))}
            {staticIPs.length === 0 && (
              <tr>
                <td colSpan={7} className="empty-message">
                  No static IPs assigned yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <StaticIPForm
          ipRangeId={ipRange.id}
          staticIP={editingIP}
          ipRange={ipRange}
          existingIPs={staticIPs.map(ip => ip.ip_address)}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Static IP"
        message={`Are you sure you want to delete static IP assignment for "${deleteConfirm.ip?.ip_address}"?\n\nDevice: ${deleteConfirm.ip?.device_name || 'N/A'}\nMAC: ${deleteConfirm.ip?.mac_address || 'N/A'}`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, ip: null })}
        variant="danger"
      />
    </div>
  );
};

export default StaticIPList;